import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router';

import { cx } from '@/lib/cx';
import { formatarData } from '@/lib/formato';
import { situacaoCertificado, type SituacaoCertificado } from '@/lib/situacao-certificado';
import { Contador } from '@/ui/components/contador';
import { Badge } from '@/ui/primitives/badge';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type EmpresaResumo } from '@fiscoai/contracts';

import { useEmpresasDoEscopo } from './api';

const FAIXAS: readonly { tipo: SituacaoCertificado['tipo']; rotulo: string; cor: string }[] = [
  { tipo: 'EM_DIA', rotulo: 'em dia', cor: 'text-sucesso' },
  { tipo: 'VENCENDO', rotulo: 'vencendo', cor: 'text-alerta' },
  { tipo: 'VENCIDO', rotulo: 'vencidos', cor: 'text-perigo' },
  { tipo: 'SEM', rotulo: 'sem certificado', cor: 'text-texto-suave' },
];

interface Linha {
  empresa: EmpresaResumo;
  situacao: SituacaoCertificado;
}

/** O que precisa de atenção primeiro: vencidos, depois o que vence antes. */
function emAtencao(linhas: readonly Linha[]): Linha[] {
  return linhas
    .filter((l) => l.situacao.tipo === 'VENCIDO' || l.situacao.tipo === 'VENCENDO')
    .sort((a, b) => (a.situacao.dias ?? 0) - (b.situacao.dias ?? 0));
}

function proximoVencimento(linhas: readonly Linha[]): EmpresaResumo | null {
  const validos = linhas.filter((l) => (l.situacao.dias ?? 0) > 0);
  validos.sort((a, b) => (a.situacao.dias ?? 0) - (b.situacao.dias ?? 0));
  return validos[0]?.empresa ?? null;
}

function Contagem({ linhas }: { linhas: readonly Linha[] }) {
  return (
    <dl className="grid grid-cols-4 gap-3">
      {FAIXAS.map((f) => (
        <div key={f.tipo} className="flex flex-col-reverse gap-0.5">
          <dt className="text-sm text-texto-suave">{f.rotulo}</dt>
          <dd className={cx('text-3xl font-semibold tabular', f.cor)}>
            <Contador valor={linhas.filter((l) => l.situacao.tipo === f.tipo).length} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Atencao({ linhas }: { linhas: readonly Linha[] }) {
  if (linhas.length === 0) return null;
  return (
    <ul className="grid gap-1.5 border-t border-borda pt-3">
      {linhas.slice(0, 4).map(({ empresa, situacao }) => (
        <li key={empresa.id}>
          <Link
            to={`/empresas/${empresa.id}?aba=certificado`}
            className="flex items-center justify-between gap-2 rounded-controle px-1 py-1 text-sm hover:bg-superficie-hover"
          >
            <span className="truncate">{empresa.nomeFantasia ?? empresa.razaoSocial}</span>
            <Badge tom={situacao.tom}>{situacao.rotulo}</Badge>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Saúde dos certificados A1 das empresas do escopo (spec 0005): sem um A1 válido a captura
 * automática da SEFAZ para — por isso o vencimento fica no radar já no Início.
 */
export function PainelCertificados({ empresaIds }: { empresaIds: readonly string[] }) {
  const empresas = useEmpresasDoEscopo(empresaIds);
  const linhas: Linha[] = (empresas.data ?? []).map((empresa) => ({
    empresa,
    situacao: situacaoCertificado(empresa.certificadoValidoAte),
  }));
  const proxima = proximoVencimento(linhas);
  const atencao = emAtencao(linhas);
  return (
    <section
      aria-label="Certificados A1"
      className="grid content-start gap-3 rounded-painel border border-borda bg-superficie p-5"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          {atencao.length > 0 ? (
            <ShieldAlert className="size-5 text-alerta" aria-hidden />
          ) : (
            <ShieldCheck className="size-5 text-sucesso" aria-hidden />
          )}
          Certificados A1
        </h2>
        {proxima?.certificadoValidoAte && (
          <span className="text-sm text-texto-suave">
            Próximo vencimento:{' '}
            <strong className="tabular text-texto">
              {formatarData(proxima.certificadoValidoAte)}
            </strong>
          </span>
        )}
      </header>
      {empresas.isPending && <Skeleton className="h-16" />}
      {empresas.isError && (
        <p className="text-sm text-texto-fraco">Não foi possível carregar os certificados agora.</p>
      )}
      {empresas.data && <Contagem linhas={linhas} />}
      <Atencao linhas={atencao} />
    </section>
  );
}
